// Overview: the answers management asks for first — how much was sold against
// target, who is leading, who is behind, new vs existing business, what the
// incentive bill is, how departments / territories / teams compare.
import { useState } from 'react';
import {
  Area, Bar as RBar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertTriangle, Award, BadgeIndianRupee, Bell, Layers, PieChart as PieIcon, Target, TrendingUp, Trophy, Users } from 'lucide-react';
import { currentMonthKey, inr, pctText, type Overview, type OverviewFilters } from '../api';
import { Bar, C, Chip, Initials, Kpi, Panel, toneFor } from './ui';

const lakh = (v: number) => (Math.abs(v) >= 10000000 ? `₹${(v / 10000000).toFixed(1)}Cr` : Math.abs(v) >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${Math.round(v / 1000)}k`);
const delta = (cur: number, prev: number) => (prev > 0 ? Math.round(((cur - prev) / prev) * 1000) / 10 : null);
const tip = { contentStyle: { borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 24px -12px rgba(15,23,42,.25)', fontSize: 12 } };

export function DashboardTab({ data, onMonth, onQuarter, onEmployee, onFilter }: {
  data: Overview; onMonth: (k: string) => void; onQuarter: (q: string) => void; onEmployee: (id: string) => void; onFilter: (p: Partial<OverviewFilters>) => void;
}) {
  const k = data.kpis;
  if (!k) return <p className="text-sm text-slate-500">No month of this financial year has started yet.</p>;
  const series = data.monthly;
  const idx = data.view === 'month' ? series.findIndex((m) => m.key === data.month) : -1;
  // No "vs last month" for the month still running — a part month always looks worse.
  const prev = idx > 0 && data.month !== currentMonthKey() ? series[idx - 1] : null;
  const cur = idx >= 0 ? series[idx] : null;
  const podium = data.rows.filter((r) => r.metrics.grossSales > 0).slice(0, 3);
  const donut = [{ name: 'New customers', value: k.newSales, color: C.green }, { name: 'Existing customers', value: k.existingSales, color: C.blue }];
  const clickMonth = (e: { activeLabel?: string } | null) => { const m = series.find((x) => x.label === e?.activeLabel); if (m) onMonth(m.key); };

  return (
    <div className="flex flex-col gap-5">
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Total sales" value={inr(k.totalSales)} icon={TrendingUp} accent={C.blue} spark={series.map((m) => m.grossSales)}
          delta={cur && prev ? delta(cur.grossSales, prev.grossSales) : null} sub={`New ${inr(k.newSales)} · Existing ${inr(k.existingSales)}`} />
        <Kpi label="Target achievement" value={pctText(k.achievementPct)} icon={Target} accent={toneFor(k.achievementPct).bar}
          progress={k.achievementPct} sub={k.target ? `${inr(k.totalSales)} of ${inr(k.target)}` : 'No targets set for this period'} />
        <Kpi label="Conversions" value={String(k.conversions)} icon={Users} accent={C.violet} spark={series.map((m) => m.conversions)}
          delta={cur && prev ? delta(cur.conversions, prev.conversions) : null} sub={`${k.newConversions} new · ${k.existingConversions} existing · win ${pctText(k.conversionRatePct)}`} />
        <Kpi label="Incentive earned" value={inr(k.incentiveEarned)} icon={BadgeIndianRupee} accent={C.green} spark={series.map((m) => m.incentive)}
          sub={`Paid ${inr(k.incentivePaid)} · Pending ${inr(k.incentivePending)}`} />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Mini label="Average monthly sales" value={inr(k.avgMonthlySales)} />
        <Mini label="Sales target" value={k.target ? inr(k.target) : '—'} />
        <Mini label={data.access.seesAll ? 'Sales employees' : 'Your grade'} value={data.access.seesAll ? String(k.employees) : (data.rows[0]?.grade || '—')} />
        <Mini label="Awaiting approval" value={String(data.pendingApprovals)} tone={data.pendingApprovals ? 'text-amber-600' : undefined} />
      </div>

      {/* Main charts */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Panel title="Target vs actual sales" subtitle="Click a month to open it" icon={TrendingUp} className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={290}>
            <ComposedChart data={series} onClick={clickMonth} margin={{ left: 0, right: 8, top: 8 }}>
              <defs><linearGradient id="actualFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.blue} stopOpacity={0.35} /><stop offset="100%" stopColor={C.blue} stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={lakh} tick={{ fontSize: 11, fill: '#64748b' }} width={58} axisLine={false} tickLine={false} />
              <Tooltip {...tip} formatter={(v: number) => inr(v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Area isAnimationActive={false} type="monotone" dataKey="grossSales" name="Actual sales" stroke={C.blue} strokeWidth={2.5} fill="url(#actualFill)" activeDot={{ r: 5, cursor: 'pointer' }} />
              <Line isAnimationActive={false} type="monotone" dataKey="target" name="Target" stroke={C.amber} strokeWidth={2} strokeDasharray="6 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="New vs existing business" subtitle="Share of sales in this period" icon={PieIcon}>
          {k.totalSales > 0 ? (
            <div className="flex flex-col items-center">
              <div className="relative h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie isAnimationActive={false} data={donut} dataKey="value" innerRadius={62} outerRadius={88} paddingAngle={2} stroke="none">
                      {donut.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip {...tip} formatter={(v: number) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xs text-slate-500">New share</span>
                  <span className="text-2xl font-bold text-slate-900">{k.totalSales ? Math.round((k.newSales / k.totalSales) * 100) : 0}%</span>
                </div>
              </div>
              <div className="mt-2 grid w-full grid-cols-2 gap-2">
                {donut.map((d) => (
                  <div key={d.name} className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="flex items-center gap-1.5 text-data-xs text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: d.color }} />{d.name}</p>
                    <p className="text-sm font-semibold text-slate-900">{inr(d.value)}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="py-16 text-center text-sm text-slate-400">No sales in this period.</p>}
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/* Podium */}
        <Panel title={data.access.seesAll ? 'Top performers' : 'Your standing'} subtitle="Ranked live by overall score" icon={Trophy}>
          {podium.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No sales yet in this period.</p> : (
            <div className="flex flex-col gap-2.5">
              {podium.map((r, i) => (
                <button key={r.employeeId} type="button" onClick={() => onEmployee(r.employeeId)}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition hover:shadow-sm ${['border-amber-200 bg-gradient-to-r from-amber-50 to-white', 'border-slate-200 bg-gradient-to-r from-slate-50 to-white', 'border-orange-200 bg-gradient-to-r from-orange-50 to-white'][i]}`}>
                  <span className="text-2xl">{['🥇', '🥈', '🥉'][i]}</span>
                  <Initials name={r.name} size={34} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{r.name}</p>
                    <p className="text-data-xs text-slate-500">{inr(r.metrics.grossSales)} · {pctText(r.metrics.achievementPct)} of target</p>
                  </div>
                  <div className="text-right"><p className="text-sm font-bold text-slate-900">{r.score ?? '—'}</p><p className="text-data-xs text-slate-500">{r.grade}</p></div>
                </button>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Conversions" subtitle="Customers billed each month and pipeline win rate" icon={Users} className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={series} onClick={clickMonth}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="c" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={34} />
              <YAxis yAxisId="p" orientation="right" unit="%" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={40} />
              <Tooltip {...tip} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <RBar isAnimationActive={false} yAxisId="c" dataKey="newConversions" name="New" stackId="c" fill={C.green} radius={[0, 0, 0, 0]} cursor="pointer" />
              <RBar isAnimationActive={false} yAxisId="c" dataKey="existingConversions" name="Existing" stackId="c" fill={C.blue} radius={[5, 5, 0, 0]} cursor="pointer" />
              <Line isAnimationActive={false} yAxisId="p" dataKey="conversionRatePct" name="Win rate %" stroke={C.amber} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {data.alerts.length > 0 && (
        <Panel title="Alerts" subtitle="Who needs attention this period" icon={Bell}>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {data.alerts.slice(0, 12).map((a, i) => (
              <div key={i} className={`flex items-start gap-2 rounded-xl px-3 py-2 text-[13px] ${a.tone === 'good' ? 'bg-emerald-50 text-emerald-800' : a.tone === 'bad' ? 'bg-rose-50 text-rose-800' : a.tone === 'warn' ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {a.text}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {data.comparisons && <Comparisons data={data} onFilter={onFilter} />}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Panel title="Quarterly performance" subtitle="Indian financial year — click a quarter" icon={Layers} className="xl:col-span-2">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.quarterly.map((q) => {
              const t = toneFor(q.achievementPct);
              return (
                <button key={q.quarter} type="button" onClick={() => onQuarter(q.quarter)} className="rounded-xl border border-slate-200 p-3.5 text-left transition hover:border-blue-300 hover:shadow-sm">
                  <div className="flex items-center justify-between"><span className="text-sm font-bold text-slate-900">{q.quarter}</span><Chip className={t.soft}>{pctText(q.achievementPct)}</Chip></div>
                  <p className="mt-2 text-lg font-bold tabular-nums text-slate-900">{inr(q.grossSales)}</p>
                  <p className="text-data-xs text-slate-500">Target {q.target ? inr(q.target) : '—'}</p>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, q.achievementPct ?? 0)}%`, background: t.bar }} /></div>
                  <p className="mt-2 text-data-xs text-slate-500">{q.conversions} conversions · incentive {inr(q.incentive)}</p>
                </button>
              );
            })}
          </div>
        </Panel>
        <Panel title="Monthly incentive" subtitle="Calculated, or projected until calculated" icon={BadgeIndianRupee}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={series} onClick={clickMonth}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip {...tip} formatter={(v: number) => inr(v)} />
              <RBar isAnimationActive={false} dataKey="incentive" name="Incentive" fill={C.green} radius={[5, 5, 0, 0]} cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Month by month" subtitle={`FY ${data.fy} · April to March`} icon={Award} pad={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
              {['Month', 'Gross sales', 'New', 'Existing', 'Conversions', 'Target', 'Achievement', 'Incentive', ''].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {series.map((m) => (
                <tr key={m.key} onClick={() => onMonth(m.key)} className={`cursor-pointer border-t border-slate-100 hover:bg-blue-50/40 ${m.key === data.month && data.view === 'month' ? 'bg-blue-50/60' : ''}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{m.label}</td>
                  <td className="px-4 py-2.5 tabular-nums font-medium">{inr(m.grossSales)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-emerald-700">{inr(m.newSales)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-blue-700">{inr(m.existingSales)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{m.conversions} <span className="text-data-xs text-slate-400">({m.newConversions} new)</span></td>
                  <td className="px-4 py-2.5 tabular-nums text-slate-500">{m.target ? inr(m.target) : '—'}</td>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><span className={`w-14 font-semibold tabular-nums ${toneFor(m.achievementPct).text}`}>{pctText(m.achievementPct)}</span><Bar pct={m.achievementPct} /></div></td>
                  <td className="px-4 py-2.5 tabular-nums">{inr(m.incentive)}</td>
                  <td className="px-4 py-2.5">{m.closed ? <Chip className="bg-slate-100 text-slate-600">Closed</Chip> : <Chip className="bg-emerald-50 text-emerald-700">Open</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Mini({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3">
      <p className="text-data-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-0.5 text-lg font-bold tabular-nums ${tone || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function Comparisons({ data, onFilter }: { data: Overview; onFilter: (p: Partial<OverviewFilters>) => void }) {
  const [by, setBy] = useState<'department' | 'territory' | 'branch' | 'manager'>('department');
  const rows = data.comparisons![by];
  const filterKey = { department: 'department', territory: 'territory', branch: 'branch', manager: 'manager' }[by] as keyof OverviewFilters;
  const managerId = (name: string) => data.filterOptions.managers.find((m) => m.name === name)?.id || '';
  return (
    <Panel title="Comparison" subtitle="Which department, territory, branch or team is doing best — click to filter" icon={Layers}
      right={(
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {(['department', 'territory', 'branch', 'manager'] as const).map((b) => (
            <button key={b} type="button" onClick={() => setBy(b)} className={`rounded-md px-2.5 py-1 text-xs font-semibold capitalize ${by === b ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}>{b === 'manager' ? 'Team' : b}</button>
          ))}
        </div>
      )}>
      {rows.length === 1 && rows[0].key === 'Not set' ? (
        <p className="py-6 text-center text-sm text-slate-500">No {by === 'manager' ? 'managers' : `${by}s`} assigned yet — set them in <b>Team Setup</b>.</p>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 38)}>
            <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="key" width={110} tick={{ fontSize: 12, fill: '#334155' }} axisLine={false} tickLine={false} />
              <Tooltip {...tip} formatter={(v: number) => inr(v)} />
              <RBar isAnimationActive={false} dataKey="sales" name="Sales" fill={C.blue} radius={[0, 6, 6, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
          <table className="w-full self-start text-[13px]">
            <thead><tr className="text-left text-data-xs uppercase tracking-wide text-slate-500"><th className="py-1.5">{by === 'manager' ? 'Team of' : by}</th><th>People</th><th className="text-right">Sales</th><th className="text-right">Achievement</th><th className="text-right">Incentive</th></tr></thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.key} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/40"
                  onClick={() => g.key !== 'Not set' && onFilter({ [filterKey]: by === 'manager' ? managerId(g.key) : g.key } as Partial<OverviewFilters>)}>
                  <td className="py-2 font-semibold capitalize text-slate-900">{g.key}</td>
                  <td className="py-2">{g.employees}</td>
                  <td className="py-2 text-right tabular-nums">{inr(g.sales)}</td>
                  <td className={`py-2 text-right font-semibold tabular-nums ${toneFor(g.achievementPct).text}`}>{pctText(g.achievementPct)}</td>
                  <td className="py-2 text-right tabular-nums">{inr(g.incentive)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

